import { normalize, schema } from 'normalizr';
import { xhrStatusFactory } from 'redux/reusable/xhrStatus';
import type { Account } from 'types/account';
import { reducerWithInitialState } from 'typescript-fsa-reducers';
import { normalizeAccountPayload } from 'utils/account';
import { wrapReducer } from './accounts.actions';
import type { State } from './accounts.types';
import * as actionTypes from './accounts.types';
import * as userActionTypes from './users.types';

const propertySchema = new schema.Entity(
  'onboardingProperties',
  {},
  {
    idAttribute: 'property',
  }
);
const propertyArraySchema = new schema.Array(propertySchema);

interface NormalizedPayload {
  entities: { onboardingProperties: actionTypes.OnboardingPropertyPayload };
}

const bootstrappedData: any = window.BOOTSTRAPPED_DATA?.account ?? {};

const initialState: State = {
  current: normalizeAccountPayload(bootstrappedData),
  entries: [],
  draft: null,
  onboardingDraft: null,
  xhrStatus: xhrStatusFactory(),
  onboardingProperties: null,
  subscriptionPlans: [],
  subscriptionAddOns: [],
  isShowingAddOnsModal: false,
  isShowingBookDemoModal: false,
  accountReportSettings: null,
  accountReportSettingsDraft: null,
  resourcePermissions: null,
  resourcePermissionDraft: null,
  accountUserProperties: [],
  accountUserPropertiesDraft: [],
  userAccountProperties: {},
  userPropertiesPreviouslyUsedValues: {},
};

const reducer = reducerWithInitialState<State>(initialState)
  .case(actionTypes.fetched, (state, payload) => {
    const { entries, currentAccountId } = payload;
    const current = entries.find(account => account.id === currentAccountId);
    return { ...state, current: { ...state.current, ...current }, entries };
  })
  .case(actionTypes.saved, (state, account) => {
    const entries = state.entries.map(item => {
      if (item.id === account.id) {
        return account;
      }
      return item;
    });
    return {
      ...state,
      current: { ...state.current, ...account },
      entries,
    };
  })
  .case(actionTypes.clearDraft, state => ({
    ...state,
    draft: null,
    accountReportSettingsDraft: null,
  }))
  .case(actionTypes.startReportSettingsDraft, (state, payload) => {
    if (!state.accountReportSettings) return state;

    if (!payload) {
      return {
        ...state,
        accountReportSettingsDraft: {
          ...state.accountReportSettings,
        },
      };
    }

    return {
      ...state,
      accountReportSettingsDraft: {
        ...state.accountReportSettings,
        ...payload,
      },
    };
  })
  .case(actionTypes.createAccountUserPropertiesDraft, state => {
    return {
      ...state,
      accountUserPropertiesDraft: state.accountUserProperties.map(field => ({
        _clientSideId: field.id,
        id: field.id,
        name: field.name,
      })),
    };
  })
  .case(actionTypes.updateReportSettingsDraft, (state, draft) => {
    if (!draft || !state.accountReportSettingsDraft) return state;

    return {
      ...state,
      accountReportSettingsDraft: {
        ...state.accountReportSettingsDraft,
        ...draft,
      },
    };
  })
  .case(actionTypes.startDraft, (state, payload) => {
    const { current } = initialState;
    return {
      ...state,
      draft: {
        ...current,
        ...payload,
        company_size: current.company_size || 0,
      },
    };
  })
  .case(actionTypes.updateDraft, (state, draft) => {
    if (!state.draft) {
      return {
        ...state,
        draft: draft as Account,
      };
    }

    return {
      ...state,
      draft: {
        ...state.draft,
        ...draft,
      },
    };
  })
  .case(actionTypes.setAccountProperties, (state, payload) => {
    const { entities }: NormalizedPayload = normalize(payload, propertyArraySchema);
    return {
      ...state,
      onboardingProperties: {
        ...state.onboardingProperties,
        ...entities.onboardingProperties,
      },
    };
  })
  .case(actionTypes.setAccountReportSettings, (state, payload) => {
    return {
      ...state,
      accountReportSettings: payload,
    };
  })
  .case(actionTypes.setAccountUserProperties, (state, payload) => {
    return {
      ...state,
      accountUserProperties: payload,
    };
  })
  .case(actionTypes.setUserAccountProperties, (state, payload) => {
    return {
      ...state,
      userAccountProperties: {
        ...state.userAccountProperties,
        [payload.userId]: payload.properties,
      },
    };
  })
  .case(actionTypes.deleteDraftAccountUserProperty, (state, clientSideId) => {
    return {
      ...state,
      accountUserPropertiesDraft: state
        .accountUserPropertiesDraft!.filter(
          f => !(f._clientSideId === clientSideId && !('id' in f))
        )
        .map(f =>
          f._clientSideId === clientSideId
            ? {
                ...f,
                _destroy: 'true',
              }
            : f
        ),
    };
  })
  .case(actionTypes.addDraftAccountUserProperty, (state, clientSideId) => {
    return {
      ...state,
      accountUserPropertiesDraft: [
        ...state.accountUserPropertiesDraft,
        { _clientSideId: clientSideId, name: '' },
      ],
    };
  })
  .case(actionTypes.setAccountUserPropertiesPreviouslyUsedValues, (state, payload) => {
    return {
      ...state,
      userPropertiesPreviouslyUsedValues: payload,
    };
  })
  .case(actionTypes.updateDraftAccountUserProperty, (state, payload) => {
    return {
      ...state,
      accountUserPropertiesDraft: [
        ...state.accountUserPropertiesDraft!.map(field =>
          field._clientSideId === payload._clientSideId ? payload : field
        ),
      ],
    };
  })
  .case(actionTypes.emulateFeatures, (state, emulatedProperties) => ({
    ...state,
    emulatedProperties: {
      ...state.emulatedProperties,
      ...emulatedProperties,
    },
  }))
  .case(actionTypes.stopEmulatingFeatures, state => ({ ...state, emulatedProperties: undefined }))
  .case(actionTypes.fetchedSubscriptionAddOns, (state, subscriptionAddOns) => ({
    ...state,
    subscriptionAddOns,
  }))
  .casesWithAction(
    [actionTypes.addSubscriptionAddOn, actionTypes.removeSubscriptionAddOn],
    (state, action) => {
      return {
        ...state,
        subscriptionAddOns: state.subscriptionAddOns.map(a =>
          a.id === action.payload.id ? action.payload : a
        ),
      };
    }
  )
  .case(actionTypes.fetchedSubscriptionPlans, (state, subscriptionPlans) => ({
    ...state,
    subscriptionPlans,
  }))
  .case(
    userActionTypes.addUser,
    (state): State => ({
      ...state,
      current: {
        ...state.current,
        num_users: state.current.num_users + 1,
      },
    })
  )
  .case(
    userActionTypes.removeUser,
    (state): State => ({
      ...state,
      current: {
        ...state.current,
        num_users: state.current.num_users - 1,
      },
    })
  )
  .case(actionTypes.toggleBookDemoModal, (state): State => {
    return {
      ...state,
      isShowingBookDemoModal: !state.isShowingBookDemoModal,
    };
  })
  .case(actionTypes.fetchedSingleResourcePermissions, (state, payload) => {
    const resourcePermissions = {
      [payload.resource]: payload.permissions,
    } as actionTypes.ResourcePermissionsState;

    if (state.resourcePermissions === null) {
      return { ...state, resourcePermissions };
    }

    return {
      ...state,
      resourcePermissions: {
        ...state.resourcePermissions,
        ...resourcePermissions,
      },
    };
  })
  .case(actionTypes.createResourcePermissionsDraft, (state, resourcePermissionDraft) => {
    return {
      ...state,
      resourcePermissionDraft,
    };
  })
  .case(actionTypes.updateResourcePermissionsDraft, (state, payload) => {
    return {
      ...state,
      resourcePermissionDraft: {
        ...state.resourcePermissionDraft,
        ...payload,
      },
    };
  })
  .case(actionTypes.resetResourcePermissionsDraft, state => ({
    ...state,
    resourcePermissionDraft: null,
  }))
  .case(actionTypes.startOnboardingDraft, (state, payload) => ({
    ...state,
    onboardingDraft: { ...payload },
  }))
  .case(actionTypes.updateOnboardingDraft, (state, payload) => ({
    ...state,
    onboardingDraft: { ...state.onboardingDraft, ...payload },
  }))
  .case(actionTypes.clearOnboardingDraft, state => ({
    ...state,
    onboardingDraft: null,
  }));

export default wrapReducer(reducer);
